import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Box, FlexBox, IconButton, Text, Timestamp, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';
import { ConversationMessageEventBox, variantConfig, } from '../messageEventBox/conversationMessageEventBox';
import styles from './conversationTextualMessage.module.scss';
import { ConversationTextualMediaMessageEvents } from './subComponents/textualMediaMessageEvents/conversationTextualMediaMessageEvents';
/**
 * Represents the maximum height of the content observer element. This height
 * determines the point at which the scroll to top button becomes visible.
 */
const contentObserverMaxHeight = 300;
/**
 * Represents the minimum height of the content element used for computing
 * whether to show the scroll to top button and whether to make the header
 * elements in the message sticky. This number should not exceed that of
 * {@link contentObserverMaxHeight}.
 */
const contentObservedMinHeight = 160;
/** Represents the component for displaying a conversation textual message. */
export const ConversationTextualMessage = ({ author, authorEndSlot, timestamp, children, variant, className, gutterActionSlot, hideGutterActions, authorDescriptionSlot, }) => {
    const rootRef = useRef(null);
    const contentRef = useRef(null);
    const observerRef = useRef(null);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [isStickyHeader, setIsStickyHeader] = useState(false);
    const authorDisplayName = useMemo(() => (author ? getUserDisplayName(author) : 'Unknown'), [author]);
    // Handles determining whether the header elements in the message should be sticky.
    useEffect(() => {
        const currentRoot = contentRef.current;
        const resizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (currentRoot && entry.target === currentRoot) {
                    setIsStickyHeader(entry.contentRect.height > contentObservedMinHeight);
                }
            });
        });
        if (currentRoot) {
            resizeObserver.observe(currentRoot);
        }
        return () => {
            if (currentRoot) {
                resizeObserver.unobserve(currentRoot);
            }
        };
    }, []);
    // Handles determining whether to show the scroll to top button.
    useEffect(() => {
        if (!isStickyHeader) {
            setShowScrollToTop(false);
            return;
        }
        const observerElement = observerRef.current;
        const intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (observerElement && entry.target === observerElement) {
                    setShowScrollToTop(entry.boundingClientRect.height > contentObservedMinHeight &&
                        !entry.isIntersecting);
                }
            });
        }, { threshold: [0, 1] });
        if (observerElement) {
            intersectionObserver.observe(observerElement);
        }
        return () => {
            if (observerElement) {
                intersectionObserver.unobserve(observerElement);
            }
            intersectionObserver.disconnect();
        };
    }, [isStickyHeader]);
    return (_jsx("div", Object.assign({ ref: rootRef, className: className }, { children: _jsx(ConversationMessageEventBox, Object.assign({ variant: variant }, { children: _jsxs(FlexBox, Object.assign({ gap: "250" }, { children: [_jsx(Avatar, { className: classnames(styles.avatar, {
                            [styles.isSticky]: isStickyHeader,
                        }), user: author }), _jsxs(FlexBox, Object.assign({ flex: "1", flexDirection: "column", minWidth: "0" }, { children: [_jsxs(Box, Object.assign({ className: classnames(styles.authorContainer, {
                                    [styles.isSticky]: isStickyHeader,
                                }), style: Object.assign({}, (isStickyHeader
                                    ? {
                                        backgroundImage: `linear-gradient(to bottom, ${variantConfig[variant].background} 80%, transparent)`,
                                    }
                                    : {})) }, { children: [_jsxs(FlexBox, Object.assign({ gap: "200" }, { children: [_jsx("strong", { children: authorDisplayName }), authorEndSlot && _jsx(Box, Object.assign({ flex: "1" }, { children: authorEndSlot }))] })), authorDescriptionSlot && _jsx(Box, { children: authorDescriptionSlot })] })), _jsxs(Box, Object.assign({ ref: contentRef, className: styles.contentContainer }, { children: [_jsx("div", { ref: observerRef, className: styles.contentObserver, style: { maxHeight: contentObserverMaxHeight } }), _jsx("div", Object.assign({ className: styles.content }, { children: children }))] }))] })), _jsxs(FlexBox, Object.assign({ alignItems: "flex-end", alignSelf: "start", className: classnames(styles.gutter, {
                            [styles.isSticky]: isStickyHeader,
                        }), flexDirection: "column", gap: "300" }, { children: [_jsx(Text.Paragraph, Object.assign({ className: styles.timestamp, variant: "4" }, { children: _jsx(Timestamp, { relative: true, time: timestamp }) })), _jsxs(FlexBox, Object.assign({ gap: "100", justifyContent: "end" }, { children: [!hideGutterActions && gutterActionSlot, showScrollToTop && (_jsx(IconButton, { icon: "arrow-up", variant: "secondary", onClick: () => {
                                            var _a;
                                            return (_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                                inline: 'nearest',
                                            });
                                        } }))] }))] }))] })) })) })));
};
ConversationTextualMessage.Events = ConversationTextualMediaMessageEvents;
